/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: RingGroupsPage.tsx (Ring Groups)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Ring Groups list page with table view, search, pagination, and actions.

 🔧 Notes:
    - Refactored from monolithic file
    - Logic delegated to hooks/
    - API calls moved to api/
    - JSX rendering for columns kept here (must be in .tsx file)

 🔁 Last Updated: 2025-01-15 by Rodrigo Cuadra (Refactored)
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Edit, Trash2, Layers3, Copy } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { useRingGroupList } from './hooks';
import { deleteRingGroupById, getRingGroupDuplicateUrl } from './ringGroupsActions';
import type { RingGroupResponse } from './types';

const STRATEGY_KEYS: Record<string, string> = {
  simultaneous: 'ringGroups.strategy.simultaneous',
  sequence: 'ringGroups.strategy.sequence',
  enterprise: 'ringGroups.strategy.enterprise',
  random: 'ringGroups.strategy.random',
  rollover: 'ringGroups.strategy.rollover'
};

export default function RingGroupsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotifications();

  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; ringGroup: RingGroupResponse | null }>({
    isOpen: false,
    ringGroup: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data,
    loading,
    columns: baseColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useRingGroupList();

  // Add render functions to columns (JSX must be in .tsx file)
  const columns = useMemo(() => {
    return baseColumns.map((column) => {
      if (column.key === 'extension') {
        return {
          ...column,
          render: (_value: unknown, item: RingGroupResponse) => (
            <span className="font-mono text-sm text-primary font-medium">
              {item?.extension ?? '—'}
            </span>
          )
        };
      }

      if (column.key === 'name') {
        return {
          ...column,
          render: (_value: unknown, item: RingGroupResponse) => (
            <div className="flex items-center gap-2">
              <Layers3 className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium text-foreground">{item?.name ?? ''}</span>
            </div>
          )
        };
      }

      if (column.key === 'strategy') {
        return {
          ...column,
          render: (_value: unknown, item: RingGroupResponse) => (
            <span className="text-sm text-muted-foreground capitalize">
              {t(STRATEGY_KEYS[item?.strategy] ?? 'ringGroups.strategy.simultaneous')}
            </span>
          )
        };
      }

      if (column.key === 'ringTimeout') {
        return {
          ...column,
          render: (_value: unknown, item: RingGroupResponse) => (
            <span className="text-sm text-muted-foreground">{item?.ringTimeout ?? 0}s</span>
          )
        };
      }

      if (column.key === 'enabled') {
        return {
          ...column,
          render: (_value: unknown, item: RingGroupResponse) => (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                item?.enabled
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
              }`}
            >
              {item?.enabled ? t('common.enabled') : t('common.disabled')}
            </span>
          )
        };
      }

      if (column.key === 'updatedAt') {
        return {
          ...column,
          render: (_value: unknown, item: RingGroupResponse) => (
            <span className="text-sm text-muted-foreground">
              {item?.updatedAt ? new Date(item.updatedAt).toLocaleString() : '—'}
            </span>
          )
        };
      }

      if (column.key === 'actions') {
        return {
          ...column,
          render: (_value: unknown, item: RingGroupResponse) => renderRowActions(item)
        };
      }

      return column;
    });
  }, [baseColumns, t]);

  const handleCreate = useCallback(() => {
    navigate('/pbx/call-center/ring-groups/new');
  }, [navigate]);

  const handleEdit = useCallback((item: RingGroupResponse) => {
    navigate(`/pbx/call-center/ring-groups/${item.uuid || item.id}`);
  }, [navigate]);

  const handleDuplicate = useCallback((item: RingGroupResponse) => {
    navigate(getRingGroupDuplicateUrl(item.uuid || item.id));
  }, [navigate]);

  const handleDeleteClick = useCallback((item: RingGroupResponse) => {
    setDeleteModal({ isOpen: true, ringGroup: item });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.ringGroup) {
      return;
    }

    setIsDeleting(true);
    try {
      const idToUse = deleteModal.ringGroup.uuid ?? deleteModal.ringGroup.id;
      await deleteRingGroupById(idToUse);

      showSuccess(t('ringGroups.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, ringGroup: null });
      refresh();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('ringGroups.messages.deleteError');
      showError(message);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.ringGroup, refresh, showError, showSuccess, t]);

  const renderRowActions = useMemo(() => (item: RingGroupResponse) => (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('common.edit')} position="top">
        <button
          onClick={() => handleEdit(item)}
          className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.duplicate')} position="top">
        <button
          onClick={() => handleDuplicate(item)}
          className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.delete')} position="top">
        <button
          onClick={() => handleDeleteClick(item)}
          className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
        </button>
      </SimpleTooltip>
    </div>
  ), [handleDeleteClick, handleDuplicate, handleEdit, t]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onAdd={handleCreate}
          onDuplicate={handleDuplicate}
          onColumnToggle={handleColumnToggle}
          title={t('ringGroups.title')}
          description={t('ringGroups.description')}
          titleIcon={<Users className="h-5 w-5 text-primary" />}
          addButtonText={t('ringGroups.actions.add')}
          renderActions={renderRowActions}
          emptyMessage={t('ringGroups.messages.emptyMessage')}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, ringGroup: null })}
        onConfirm={handleDeleteConfirm}
        title={t('common.deleteConfirmation')}
        message={t('ringGroups.messages.deleteConfirm')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}
